import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DynamicsChartFrame } from './DynamicsChartFrame';
import { DYNAMICS_CARD_INSIGHTS } from './dynamicsInsights';
import { DYNAMICS_RECHARTS_TOOLTIP_COMMON } from './dynamicsRechartsTooltip';
import { useDynamicsPlotHeight } from './DynamicsChartLayoutContext';
import { ResolvedPeriod } from '../../PeriodPicker';
import { dynamicsBucketChartMargins, dynamicsPeriodAxisProps } from './dynamicsPeriodAxis';

interface Props {
  groupBy: 'sprint' | 'release' | 'week' | 'month';
  lastN: number;
  resolvedPeriod: ResolvedPeriod | null;
  onDataStatus?: (s: { metric: string; hasData: boolean; loading: boolean }) => void;
}

function EscapedChartBody({
  points,
  onPointClick,
}: {
  points: Array<Record<string, number | string | null>>;
  onPointClick?: (bucketLabel: string) => void;
}) {
  const plotH = useDynamicsPlotHeight(300);
  return (
    <ResponsiveContainer width="100%" height={plotH}>
      <BarChart
        data={points}
        margin={{ ...dynamicsBucketChartMargins }}
        style={onPointClick ? { cursor: 'pointer' } : undefined}
        onClick={(e) => {
          const label = (e as { activeLabel?: string } | null | undefined)?.activeLabel;
          if (onPointClick && typeof label === 'string') onPointClick(label);
        }}
      >
        <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
        <XAxis {...dynamicsPeriodAxisProps} />
        <YAxis />
        <Tooltip {...DYNAMICS_RECHARTS_TOOLTIP_COMMON} />
        <Legend />
        <Bar dataKey="escaped" name="Escaped defects" fill="#ef4444" />
      </BarChart>
    </ResponsiveContainer>
  );
}

export const DynamicsE_EscapedDefects: React.FC<Props> = ({
  groupBy,
  lastN,
  resolvedPeriod,
  onDataStatus,
}) => {
  return (
    <DynamicsChartFrame
      id="E"
      title="Escaped defects per release"
      hint="Bugs filed AFTER release date that reference this fix_version (or affected_versions). A direct production-quality metric leadership cares about."
      needs="fix_version / affected_versions on bugs. Use Group by = Release for the clearest read."
      metric="escaped"
      groupBy={groupBy}
      lastN={lastN}
      resolvedPeriod={resolvedPeriod}
      onDataStatus={onDataStatus}
      chartHeight={300}
      insightsTitle={DYNAMICS_CARD_INSIGHTS.E.title}
      insights={[...DYNAMICS_CARD_INSIGHTS.E.bullets]}
    >
      {(points, onPointClick) => <EscapedChartBody points={points} onPointClick={onPointClick} />}
    </DynamicsChartFrame>
  );
};
